import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { accordionAnimation } from '../animations/animations';
import { ConfirmmodalserviceService } from '../confirm-delete-modal/confirmmodalservice.service';
import { ContactDetailDeleteRequestDTO, ContactDetailDTO, ContactDetailReadByMasterIdRequestDTO, ContactDetailUpdateRequestDTO } from '../contact-detail/contact-detail.interface';
import { ContactDetailService } from '../contact-detail/contact-detail.service';
import { atLeastOneFieldRequiredValidator } from '../validators/atLeastOneFieldRequiredValidator';
import { Subject, takeUntil } from 'rxjs';
import { ValueListService } from 'src/app/Common/ValueListService/value-list.service';
import { ValueListItemDTO } from 'src/app/Common/ValueListService/ValueListItem.interface';


@Component({
  selector: 'app-contact-detail',
  templateUrl: './contact-detail.component.html',
  styleUrls: ['./contact-detail.component.css'],
  animations: [accordionAnimation]
})
export class ContactDetailComponent implements OnInit, OnChanges, OnDestroy {
  contactService = inject(ContactDetailService);
  User = inject(UserLoginService).User();
  @Input() editMode: boolean = false;

  @Input() calledInUIC: boolean = false;
  @Input() consignmentForm: boolean = false;

  /**
   * Shows the "Contact Type" dropdown (fed from a Value List).
   * Kept false by default so Supplier / Customer / Company pages are unaffected.
   * Staff Detail page passes true.
   */
  @Input() showContactType: boolean = false;

  /**
   * Value List code backing the Contact Type dropdown.
   * Staff Detail page uses 'ICT' (Individual Contact Type).
   */
  @Input() contactTypeVlCode: string = 'ICT';

  /**
   * Shows the "Designation" field.
   */
  @Input() showDesignation: boolean = true;

  /**
   * Label for the Designation field. The Staff Detail page reuses this field as
   * "Relationship" (Self / Spouse / Guardian ...), which persists to the same
   * ContactDetail.Designation column.
   */
  @Input() designationLabel: string = 'Designation';

  /** Placeholder for the Designation / Relationship field. */
  @Input() designationPlaceholder: string = 'e.g. Purchase Manager';

  /** Bootstrap icon class used for the Designation / Relationship chip on the card. */
  @Input() designationIcon: string = 'bi-briefcase-fill';

  /**
   * Renders the Designation / Relationship field as a value-list dropdown instead
   * of a free-text input. The Staff Detail page passes true to get a Relationship
   * dropdown; Supplier / Customer keep the free-text Designation input.
   */
  @Input() designationAsDropdown: boolean = false;

  /**
   * Value List code backing the Relationship dropdown.
   * 'ICR' = Individual Contact Relationship.
   */
  @Input() designationVlCode: string = 'ICR';

  /**
   * Value List code for the plain Designation field (Supplier / Customer / Company).
   * When this value list has items the field renders as a dropdown; when it is
   * missing or empty the free-text input is kept.
   */
  @Input() designationListVlCode: string = 'CDES';

  /**
   * Makes the Designation / Relationship field mandatory. Default false so the
   * optional Designation on Supplier / Customer / Company is unaffected; the
   * Staff Detail page passes true to require Relationship.
   */
  @Input() designationRequired: boolean = false;

  @Output() ContactDetailFormEmit: EventEmitter<ContactDetailDTO[]> = new EventEmitter()
  toastr = inject(ToastrService);
  valuelistService = inject(ValueListService);
  loading: boolean = false;
  isDataLoading: boolean = false;
  designationList: string[] = ['Cont. Person Finance', 'Cont. Person Purchase', 'Collection Staff Name', 'Sales Person']

  /**
   * Options for the Contact Type dropdown. Sourced from contactTypeVlCode, with a
   * hard-coded fallback when that value list is not configured.
   */
  contactTypeOptions: string[] = [];

  /** Options for the Relationship dropdown, sourced from designationVlCode. */
  relationshipOptions: string[] = [];

  /** Options for the plain Designation field, sourced from designationListVlCode. Empty = free text. */
  designationOptions: string[] = [];

  /** Hard-coded fallbacks, used only when the matching value list returns no items. */
  readonly defaultContactTypes: string[] = [
    'Primary',
    'Alternate',
    'Emergency',
    'Nominee',
    'Other'
  ];

  readonly defaultRelationships: string[] = [
    'Self',
    'Spouse',
    'Father',
    'Mother',
    'Son',
    'Daughter',
    'Brother',
    'Sister',
    'Father-in-law',
    'Mother-in-law',
    'Guardian',
    'Friend',
    'Other'
  ];

  // Add view toggle functionality
  currentView: string = 'card';
  modalService = inject(NgbModal);
  private confirmModal = inject(ConfirmmodalserviceService)

  constructor(private toaster: ToastrService) { }

  @Input({ required: true }) masterType: string = '';
  @Input({ required: true }) masterId: number = 0;

  ContactList: ContactDetailDTO[] = [];
  PHONE_INTERNATIONAL_REGEX = /^(?:\+|00)?(?:[1-9]\d{0,4})? ?(?:\(?\d{1,4}\)?[\s.-]?)?\d{3,4}[\s.-]?\d{4}$/;

  // Enhanced form with comprehensive validation
  contactDetailForm = new FormGroup({
    contactId: new FormControl(0),
    masterId: new FormControl(0),
    masterType: new FormControl(''),
    personName: new FormControl('', [
      Validators.required
    ]),
    contactType: new FormControl('', [
      Validators.maxLength(100)
    ]),
    // ContactDetail.Designation is VARCHAR(50) in SQL - keep the validator in
    // step with the column so long values fail in the form, not at the database.
    designation: new FormControl('', [
      Validators.maxLength(50)
    ]),
    phone: new FormControl('', [
     Validators.pattern(this.PHONE_INTERNATIONAL_REGEX)
    ]),
    mobile: new FormControl('',[
      Validators.pattern(this.PHONE_INTERNATIONAL_REGEX)
    ]),
    emailId: new FormControl('', [
      Validators.email,
      Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
      // Validators.required
    ]),
    contactStatus: new FormControl(0),
    isActive: new FormControl(1),
    isDefault: new FormControl(0),
    actionUser: new FormControl(''),
    otherDetail1: new FormControl<string | null>(null),
    otherDetail2: new FormControl<string | null>(null),
    otherDetail3: new FormControl<string | null>(null),
    otherDetail4: new FormControl<string | null>(null),
    otherDetail5: new FormControl<string | null>(null),
    otherDetail6: new FormControl<string | null>(null)
  }, {
    validators: atLeastOneFieldRequiredValidator(['personName', 'contactType', 'designation', 'phone', 'mobile', 'emailId'])
  });

  /**
   * Whether the card header has any chips to show. When it doesn't, the name is
   * vertically centred instead of sitting above empty space.
   */
  hasHeaderChips(contact: ContactDetailDTO): boolean {
    const hasType = this.showContactType && !!(contact.contactType ?? '').trim();
    return hasType || contact.isDefault === 1;
  }

  /**
   * True when a contact type denotes an emergency contact, which is highlighted
   * in red on the card. Matched loosely so value-list wording such as
   * "Emergency" or "Emergency Contact" is picked up regardless of casing.
   */
  isEmergency(contactType: string | null | undefined): boolean {
    return (contactType ?? '').trim().toLowerCase().includes('emergency');
  }

  /**
   * Normalises the isDefault checkbox value (which binds as boolean) into the
   * 0/1 integer flag the API and DB column expect.
   */
  private toFlag(value: unknown): number {
    return value === true || value === 1 || value === '1' ? 1 : 0;
  }

  /**
   * List-view columns. Contact Type / Designation are swapped in based on which
   * of the two the host page has chosen to show.
   */
  get listColumns(): { colName: string; colData: string }[] {
    const columns: { colName: string; colData: string }[] = [
      { colName: 'Person Name', colData: 'personName' }
    ];
    if (this.showDesignation) {
      columns.push({ colName: this.designationLabel, colData: 'designation' });
    }
    if (this.showContactType) {
      columns.push({ colName: 'Contact Type', colData: 'contactType' });
    }
    columns.push(
      { colName: 'Mobile', colData: 'mobile' },
      { colName: 'Phone', colData: 'phone' },
      { colName: 'Email', colData: 'emailId' },
      { colName: 'Default', colData: 'isDefault' }
    );
    return columns;
  }

  ngOnInit(): void {
    if (this.showContactType) {
      this.getContactType();
    }
    if (this.showDesignation && this.designationAsDropdown) {
      this.getRelationship();
    } else if (this.showDesignation && !this.consignmentForm) {
      this.getDesignation();
    }
    // Applied here rather than in the FormGroup definition so that pages which
    // treat this field as an optional Designation keep their current behaviour.
    if (this.showDesignation && this.designationRequired) {
      const designation = this.contactDetailForm.controls.designation;
      designation.addValidators(Validators.required);
      designation.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * Loads the Contact Type options from the Value List identified by contactTypeVlCode.
   * Served from the pre-warmed ValueListService cache, so no extra network call.
   */
  getContactType() {
    this.valuelistService.getItemByVLCode(this.contactTypeVlCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.contactTypeOptions = this.toOptions(res.valueListItems, this.defaultContactTypes);
        },
        error: (err) => {
          console.error(`Error loading contact types for VlCode ${this.contactTypeVlCode}:`, err);
          this.contactTypeOptions = [...this.defaultContactTypes];
        }
      });
  }

  /** Loads the Relationship options from the Value List identified by designationVlCode. */
  getRelationship() {
    this.valuelistService.getItemByVLCode(this.designationVlCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.relationshipOptions = this.toOptions(res.valueListItems, this.defaultRelationships);
        },
        error: (err) => {
          console.error(`Error loading relationships for VlCode ${this.designationVlCode}:`, err);
          this.relationshipOptions = [...this.defaultRelationships];
        }
      });
  }

  /**
   * Loads the Designation options from the Value List identified by designationListVlCode.
   * No fallback list: an empty result keeps the free-text input.
   */
  getDesignation() {
    this.valuelistService.getItemByVLCode(this.designationListVlCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.designationOptions = this.toOptions(res.valueListItems, []);
        },
        error: (err) => {
          console.error(`Error loading designations for VlCode ${this.designationListVlCode}:`, err);
          this.designationOptions = [];
        }
      });
  }

  /**
   * Maps value list items to their display names, falling back to the hard-coded
   * list when the value list is missing or has no usable items.
   */
  private toOptions(items: ValueListItemDTO[] | undefined, fallback: string[]): string[] {
    const names = (items ?? [])
      .map(i => (i.vliName ?? '').trim())
      .filter(n => n.length > 0);
    return names.length ? names : [...fallback];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['masterId']) {
      if (this.calledInUIC) {
        this.currentView = 'list';
        this.editMode = false;
        
        // UIC Mode: Load contacts only if masterId > 0 (edit mode)
        if (this.masterId && this.masterId > 0) {
          this.loadContactsForEdit();
        } else {
          // New customer - just initialize with empty list
          this.ContactList = [];
        }
      } else {
        // Normal mode: Load contacts from API only if masterId > 0
        if (this.masterId && this.masterId > 0) {
          this.loadContactDetails();
        }
      }
    }
  }

  destroy$ = new Subject<void>();
  loadContactsForEdit() {
    let data: ContactDetailReadByMasterIdRequestDTO = {
      masterId: this.masterId,
      masterType: this.masterType,
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId
    };

    this.contactService.ContactDetail_readByMasterId(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          // ✅ Load existing contacts into local list
          this.ContactList = res.items || [];
          console.log('Loaded contacts for edit:', this.ContactList);

          // ✅ Emit loaded contacts to parent
          this.emitContacts();
        },
        error: (err) => {
          console.error('Error loading contacts:', err);
          this.ContactList = [];
        }
      });
  }


  handleActionClick(event: { actionName: string; rowData: ContactDetailDTO }) {
    const { actionName, rowData } = event;
    if (actionName === 'Edit') {
      this.editMode = true;
      this.contactDetailForm.patchValue(rowData);
    } else if (actionName === 'Default') {
      this.defaultCheckBox({ rowData, isChecked: rowData.isDefault !== 1 });
    } else if (actionName === 'Delete') {
      const label = rowData.personName || rowData.contactType || rowData.mobile || rowData.phone || rowData.emailId || 'this contact';
      this.confirmModal.openDeleteModal(label, event.rowData).subscribe((res) => {
        if (res) {
          if (this.calledInUIC) {
            if (rowData.contactId && rowData.contactId > 0) {
              // UIC Mode, persisted contact: delete via API first, then drop from local list
              const data: ContactDetailDeleteRequestDTO = {
                contactId: rowData.contactId,
                actionUser: String(this.User.userId),
                tenantId: this.User.companyId,
                tenantBranchId: this.User.loggedInBranchId
              };
              this.contactService.ContactDetail_delete(data).subscribe({
                next: () => {
                  const index = this.ContactList.findIndex(c => c.contactId === rowData.contactId);
                  if (index >= 0) this.ContactList.splice(index, 1);
                  this.toastr.success('Contact removed');
                },
                error: (err) => {
                  console.error('Error deleting contact details:', err);
                }
              });
            } else {
              // UIC Mode, unsaved (temp-id) contact: remove from local list only
              const index = this.ContactList.findIndex(c => c.contactId === event.rowData.contactId);
              if (index >= 0) {
                this.ContactList.splice(index, 1);
                this.toastr.success('Contact removed');
                this.emitContacts();  // Emit updated contact list
              }
            }
          } else {
            // Normal mode: Call API
            const data: ContactDetailDeleteRequestDTO = {
              contactId: event.rowData.contactId ?? 0,
              actionUser: String(this.User.userId),
              tenantId: this.User.companyId,
              tenantBranchId: this.User.loggedInBranchId
            };
            this.contactService.ContactDetail_delete(data).subscribe({
              next: (res) => {
                this.loadContactDetails()
              },
              error: (err) => {
                console.error('Error deleting contact details:', err);
              }
            })
          }
        }
      })
    }
  }

  submitForm() {
    this.contactDetailForm.markAllAsTouched();
    if (this.contactDetailForm.invalid) {
      console.log('Form is invalid');
      return;
    }

    const formValue = this.contactDetailForm.value;
    let payload: ContactDetailUpdateRequestDTO = {
      contactId: formValue.contactId!,
      masterId: formValue.masterId ?? 0,
      masterType: formValue.masterType ?? '',
      personName: formValue.personName ?? '',
      contactType: formValue.contactType ?? '',
      designation: formValue.designation ?? '',
      phone: formValue.phone ?? '',
      mobile: formValue.mobile ?? '',
      emailId: formValue.emailId ?? '',
      contactStatus: formValue.contactStatus ?? 0,
      isActive: 1,
      actionUser: this.User.userId.toString(),
      isDefault: this.toFlag(formValue.isDefault),
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId,
      otherDetail1: formValue.otherDetail1 ?? '',
      otherDetail2: formValue.otherDetail2 ?? '',
      otherDetail3: formValue.otherDetail3 ?? '',
      otherDetail4: formValue.otherDetail4 ?? '',
      otherDetail5: formValue.otherDetail5 ?? '',
      otherDetail6: formValue.otherDetail6 ?? ''
    }
    if (formValue.contactId && formValue.contactId > 0) {
      // Update mode - existing, already-persisted contact: call API directly
      this.updateContactDetails(payload);
      this.editMode = false;
    } else if (this.calledInUIC && this.masterId > 0) {
      // UIC Mode with existing masterId (editing company): Create immediately via API
      this.createContactDetails(payload);
      this.currentView = 'list';
      this.editMode = false;
    } else if (this.calledInUIC && this.masterId === 0) {
      // UIC Mode without masterId (new company): contactId is either 0 (brand-new) or a
      // negative temp ID (editing a contact that was only added to the local list, not the
      // DB - it has no real record to PATCH, so it must be updated locally instead).
      const existingIndex = formValue.contactId
        ? this.ContactList.findIndex(c => c.contactId === formValue.contactId)
        : -1;

      if (existingIndex >= 0) {
        this.ContactList[existingIndex] = { ...this.ContactList[existingIndex], ...payload };
        this.toastr.success('Contact updated');
      } else {
        const tempId = -(this.ContactList.length + 1);
        const newContact = { ...payload, contactId: tempId, isDeleted: 0 } as ContactDetailDTO;
        this.ContactList.push(newContact);
        this.toastr.success('Contact added');
      }
      this.currentView = 'list';
      this.emitContacts();
      this.editMode = false;
    } else {
      // Normal mode: Call API
      this.createContactDetails(payload);
      this.editMode = false;
    }
    this.contactDetailForm.reset();
  }
  defaultCheckBox(event: { rowData: ContactDetailDTO, isChecked: boolean; }) {
    let { rowData, isChecked } = event;
    let payload: ContactDetailUpdateRequestDTO = {
      contactId: rowData.contactId ?? 0,
      masterId: rowData.masterId ?? 0,
      masterType: rowData.masterType ?? "",
      personName: rowData.personName ?? "",
      contactType: rowData.contactType ?? "",
      designation: rowData.designation ?? "",
      phone: rowData.phone ?? "",
      mobile: rowData.mobile ?? "",
      emailId: rowData.emailId ?? "",
      contactStatus: rowData.contactStatus ?? 0,
      isActive: 1,
      isDefault: isChecked ? 1 : 0,
      actionUser: this.User.userId.toString(),
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId,
      otherDetail1: rowData.otherDetail1 ?? '',
      otherDetail2: rowData.otherDetail2 ?? '',
      otherDetail3: rowData.otherDetail3 ?? '',
      otherDetail4: rowData.otherDetail4 ?? '',
      otherDetail5: rowData.otherDetail5 ?? '',
      otherDetail6: rowData.otherDetail6 ?? ''
    }

    if (this.calledInUIC) {
      // UIC Mode: Update local list
      const index = this.ContactList.findIndex(c => c.contactId === rowData.contactId);
      if (index >= 0) {
        this.ContactList[index] = { ...this.ContactList[index], isDefault: isChecked ? 1 : 0 };
        
        // If it's an existing contact (contactId > 0), call API immediately
        if (rowData.contactId && rowData.contactId > 0) {
          this.contactService.ContactDetail_update(payload).subscribe({
            next: (res) => {
              this.toastr.success('Default contact updated');
            },
            error: (err) => {
              console.error('Error updating contact:', err);
            }
          });
        } else {
          // For new contacts (temp ID), just emit
          this.toastr.success('Default contact updated');
          this.emitContacts();
        }
      }
    } else {
      // Normal mode: Call API
      this.contactService.ContactDetail_update(payload).subscribe({
        next: (res) => {
          this.loadContactDetails()
        },
        error: (err) => {
          console.error('Error updating contact:', err);
        }
      })
    }
  }
  addContact() {
    this.editMode = true;
    this.contactDetailForm.reset({
      actionUser: this.User.userId.toString(),
      masterId: this.masterId,
      masterType: this.masterType,
      contactId: 0,
      isActive: 1,
      isDefault: 0,
      contactStatus: 0
    });
  }



  // Database Fucntions
  loadContactDetails() {
    if (!this.masterId || !this.masterType) return;
    const request: ContactDetailReadByMasterIdRequestDTO = {
      masterId: this.masterId,
      masterType: this.masterType,
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId
    };
    this.isDataLoading = true;
    this.contactService.ContactDetail_readByMasterId(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.ContactList = res.items ?? [];
          this.isDataLoading = false;
        },
        error: (err) => {
          console.error('Error loading contacts:', err);
          this.isDataLoading = false;
        }
      });
  }

  updateContactDetails(data: ContactDetailUpdateRequestDTO) {
    this.contactService.ContactDetail_update(data).subscribe({
      next: (res) => this.loadContactDetails(),
      error: (err) => console.error('Error updating contact:', err)
    });
  }

  createContactDetails(data: ContactDetailUpdateRequestDTO) {
    this.contactService.ContactDetail_create(data).subscribe({
      next: (res) => {
        if (this.calledInUIC) {
          // In UIC mode, add to local list instead of reloading
          this.ContactList.push(res);
          this.toastr.success('Contact created');
        } else {
          this.loadContactDetails();
        }
      },
      error: (err) => console.error('Error creating contact:', err)
    });
  }

  // Emit contacts to parent (used by UIC form)
  emitContacts() {
    if (this.calledInUIC) {
      // Only emit contacts that have temp IDs (contactId < 0)
      const newContacts = this.ContactList.filter(c => c.contactId! < 0);
      this.ContactDetailFormEmit.emit(newContacts);
    }
  }

  deleteContactDetails(data: ContactDetailDeleteRequestDTO) {
    if (!this.calledInUIC) {
      this.contactService.ContactDetail_delete(data).subscribe({
        next: (res) => this.loadContactDetails(),
        error: (err) => console.error('Error deleting contact:', err)
      })
    }
  }

  resetForm() {
    this.editMode = false;
    this.contactDetailForm.reset();
  }

  // Get new contacts to create (contacts with temp IDs)
  getNewContacts(): ContactDetailDTO[] {
    if (this.calledInUIC) {
      return this.ContactList.filter(c => c.contactId! < 0);
    }
    return [];
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

}